import { useState, useEffect } from 'react';
import { adminMatchesAPI } from '../../lib/admin-api';

import UserSearchInput from '../../components/admin/UserSearchInput';

export default function AdminForcedMatchPage() {
  const [selectedUser, setSelectedUser] = useState<{ id: number; username: string; email: string } | null>(null);
  const [selectedTargetUser, setSelectedTargetUser] = useState<{ id: number; username: string; email: string } | null>(null);
  const [priority, setPriority] = useState(5);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [forcedMatches, setForcedMatches] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);

  useEffect(() => {
    loadForcedMatches();
  }, []);

  const loadForcedMatches = async () => {
    try {
      const data = await adminMatchesAPI.getForcedMatches('all');
      setForcedMatches(data.forced_matches);
    } catch (err) {
      console.error('Failed to load forced matches:', err);
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !selectedTargetUser) {
      setError('Please select both users');
      return;
    }

    if (selectedUser.id === selectedTargetUser.id) {
      setError('Cannot match a user with themselves');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const data = await adminMatchesAPI.createForcedMatch({
        user_id: selectedUser.id,
        target_user_id: selectedTargetUser.id,
        priority,
        reason: reason || 'Admin recommendation',
      });

      setSuccess(data.message);
      setSelectedUser(null);
      setSelectedTargetUser(null);
      setReason('');
      setPriority(5);

      // Reload forced matches
      loadForcedMatches();
    } catch (err: any) {
      setError(err.message || 'Failed to create forced match');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id: number) => {
    if (!confirm('Are you sure you want to cancel this forced match?')) return;

    try {
      await adminMatchesAPI.cancelForcedMatch(id);
      loadForcedMatches();
    } catch (err: any) {
      alert('Failed to cancel: ' + err.message);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Forced Match Management</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Create Form */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Create Forced Match</h2>

            {success && (
              <div className="alert alert-success mb-4">
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="alert alert-error mb-4">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <UserSearchInput
                label="User (who will see the recommendation)"
                onSelect={(user) => setSelectedUser(user)}
                selectedUser={selectedUser}
                placeholder="Search user by name or email..."
              />

              <UserSearchInput
                label="Target User (who will be recommended)"
                onSelect={(user) => setSelectedTargetUser(user)}
                selectedUser={selectedTargetUser}
                placeholder="Search target user by name or email..."
              />

              <div className="form-control mb-4">
                <label className="label">
                  <span className="label-text">Priority (1-10, higher = more important)</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  className="range"
                  value={priority}
                  onChange={(e) => setPriority(parseInt(e.target.value))}
                />
                <div className="w-full flex justify-between text-xs px-2 mt-1">
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                </div>
                <div className="text-center font-bold mt-2">Priority: {priority}</div>
              </div>

              <div className="form-control mb-6">
                <label className="label">
                  <span className="label-text">Reason (optional)</span>
                </label>
                <textarea
                  className="textarea textarea-bordered"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., VIP user, Special event..."
                />
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-full ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Forced Match'}
              </button>
            </form>
          </div>
        </div>

        {/* Forced Matches List */}
        <div className="card bg-base-100 shadow-xl">
          <div className="card-body">
            <h2 className="card-title">Active Forced Matches</h2>

            {loadingMatches ? (
              <div className="flex justify-center py-8">
                <span className="loading loading-spinner"></span>
              </div>
            ) : forcedMatches.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                No forced matches found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Target</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forcedMatches.map((match) => (
                      <tr key={match.id}>
                        <td>{match.user_username || `#${match.user_id}`}</td>
                        <td>{match.target_username || `#${match.target_user_id}`}</td>
                        <td>
                          <span className={`badge ${match.priority >= 8 ? 'badge-error' :
                              match.priority >= 5 ? 'badge-warning' :
                                'badge-ghost'
                            }`}>
                            {match.priority}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${match.status === 'completed' ? 'badge-success' :
                              match.status === 'cancelled' ? 'badge-error' :
                                'badge-info'
                            }`}>
                            {match.status}
                          </span>
                        </td>
                        <td>
                          {match.status === 'active' && (
                            <button
                              className="btn btn-xs btn-error"
                              onClick={() => handleCancel(match.id)}
                            >
                              Cancel
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
